/**
 * One-time import from legacy crashSyncDB (utils.js) into Dexie.
 */

import { DB_NAME, STORE_NAME } from './legacy-idb-names'
import { offlineDb } from './offline-db'
import { generateClientUuid } from './uuid'

const MIGRATION_FLAG = 'adams_legacy_idb_migrated_v1'

export function wasLegacyMigrated() {
  try {
    return localStorage.getItem(MIGRATION_FLAG) === '1'
  } catch {
    return false
  }
}

function setMigrated() {
  try {
    localStorage.setItem(MIGRATION_FLAG, '1')
  } catch {
    /* ignore */
  }
}

function openLegacyDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2)
    request.onerror = () => reject(new Error('legacy IDB open failed'))
    request.onsuccess = () => resolve(request.result)
  })
}

/**
 * @returns {Promise<void>}
 */
export async function migrateLegacyPendingToDexie() {
  if (wasLegacyMigrated()) return
  let db
  try {
    db = await openLegacyDb()
  } catch {
    setMigrated()
    return
  }
  try {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      setMigrated()
      return
    }
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const clientUuid = generateClientUuid()
      const payload = { ...row }
      if ('id' in payload) delete payload.id
      await offlineDb.localIncidents.put({
        clientUuid,
        payload,
        status: 'queued',
        updatedAt: new Date().toISOString()
      })
      await offlineDb.syncQueue.add({
        clientUuid,
        operation: 'final_submit',
        payload,
        idempotencyKey: generateClientUuid(),
        attempts: 0,
        status: 'pending',
        createdAt: new Date().toISOString(),
        nextRetryAt: null
      })
    }
    if (rows.length > 0) {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const req = tx.objectStore(STORE_NAME).clear()
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
      })
    }
  } finally {
    try {
      db.close()
    } catch {
      /* ignore */
    }
  }
  setMigrated()
}
